import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Button, Input, message, Space, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PlusOutlined } from '@ant-design/icons'
import axios from 'axios'
import { ENSEIGNE_OPTIONS, SPECIALITE_OPTIONS } from './OrganisationParams'

export type SoftwareKind = 'cn' | 'conquest'

export interface OrganisationSoftware {
  label: string
  kind: SoftwareKind
}

export interface OrganisationRow {
  orga_id: string
  name: string
  collaborators_count: number
  siret: string | null
  softwares: OrganisationSoftware[]
  specialites: string[]
  enseignes: string[]
}

const kindColor: Record<SoftwareKind, string> = {
  cn: 'blue',
  conquest: 'orange',
}

export function OpenOrganisations() {
  const history = useHistory()
  const [organisations, setOrganisations] = useState<OrganisationRow[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  const loadOrganisations = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await axios.get<OrganisationRow[]>('/organisations/list')
      setOrganisations(data)
    } catch (error) {
      console.error(error)
      message.error('Impossible de charger les organisations')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadOrganisations()
  }, [loadOrganisations])

  const filteredOrganisations = useMemo(() => {
    // Filter : case insensitive + accent insensitive + space insensitive
    const value = search.trim()
      .toLowerCase()
      .normalize('NFD') // Normalize the string to remove accents
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/\s+/g, '') // Remove spaces
    if (!value) return organisations
    return organisations.filter(orga => orga.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '').includes(value))
  }, [organisations, search])

  const columns: ColumnsType<OrganisationRow> = useMemo(
    () => [
      {
        title: 'Dénomination',
        dataIndex: 'name',
        key: 'name',
        width: 300,
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (name: string, record: OrganisationRow) =>
          <Space direction="vertical" size={0}>
            <Typography.Text>{name}</Typography.Text>
            {record.siret && <Typography.Text type="secondary" style={{ fontSize: 11 }}>{record.siret}</Typography.Text>}
          </Space>
      },
      {
        title: 'Collaborateurs',
        dataIndex: 'collaborators_count',
        key: 'collaborators_count',
        width: 180,
        sorter: (a, b) => a.collaborators_count - b.collaborators_count,
      },
      {
        title: 'Logiciels',
        dataIndex: 'softwares',
        key: 'softwares',
        filters: [
          { text: 'Scan & Conquest', value: 'cn' },
          { text: 'Conquest', value: 'conquest' },
        ],
        onFilter: (value, record) => record.softwares.some(soft => soft.kind === value),
        render: (softwares: OrganisationSoftware[]) =>
          softwares.length === 0 ? (
            '-'
          ) : (
            <Space size={[4, 4]} wrap>
              {softwares.map((soft, index) => (
                <Tag key={`${soft.kind}-${index}`} color={kindColor[soft.kind] || 'default'}>
                  {soft.label}
                </Tag>
              ))}
            </Space>
          ),
      },
      {
        title: 'Spécialités',
        dataIndex: 'specialites',
        key: 'specialites',
        width: 200,
        filters: SPECIALITE_OPTIONS.map(value => ({ text: value, value })),
        onFilter: (value, record) => record.specialites.includes(String(value)),
        render: (specialites: string[] = []) =>
          specialites.length === 0 ? (
            '-'
          ) : (
            <Space size={[4, 4]} wrap>
              {specialites.map(tag => (
                <Tag key={tag} color="magenta">
                  {tag}
                </Tag>
              ))}
            </Space>
          ),
      },
      {
        title: 'Enseignes',
        dataIndex: 'enseignes',
        key: 'enseignes',
        width: 200,
        filters: ENSEIGNE_OPTIONS.map(value => ({ text: value, value })),
        onFilter: (value, record) => record.enseignes.includes(String(value)),
        render: (enseignes: string[] = []) =>
          enseignes.length === 0 ? (
            '-'
          ) : (
            <Space size={[4, 4]} wrap>
              {enseignes.map(tag => (
                <Tag key={tag} color="geekblue">
                  {tag}
                </Tag>
              ))}
            </Space>
          ),
      },
    ],
    [],
  )

  return (
    <div style={{ marginTop: 16 }}>
      <Space align="center" style={{ marginBottom: 24, width: '100%', justifyContent: 'space-between' }} wrap>
        <Input
          autoFocus
          allowClear
          placeholder="Rechercher une organisation"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 260 }}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => history.push('/organisations/new')}>
          Ajouter une organisation
        </Button>
      </Space>

      <Table
        rowKey="orga_id"
        loading={loading}
        columns={columns}
        dataSource={filteredOrganisations}
        scroll={{ x: 1100 }}
        tableLayout="fixed"
        pagination={{ pageSize: 50, showSizeChanger: false }}
        onRow={record => ({
          onClick: () => {
            window.location.href = `/organisations/${record.orga_id}/params`
          },
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  )
}
